import React from 'react';
import dayjs from 'dayjs';
import type {
  InitialInspectionFindingsRow,
  InitialInspectionPartLineRow,
  InitialInspectionRequest,
} from '@/services/qc-workflow.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';

interface PrintableInitialInspectionReportProps {
  iir?: InitialInspectionRequest;
  data?: InitialInspectionRequest;
  report?: InitialInspectionRequest;
  onClose?: () => void;
}

const safeRows = <T extends object>(rows: T[] | null | undefined): T[] => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return rows;
};

/** Initial inspection report — shared pagination engine with restored findings/parts. */
export const PrintableInitialInspectionReport: React.FC<PrintableInitialInspectionReportProps> = (
  props
) => {
  const iir = (props.iir || props.data || props.report || {}) as InitialInspectionRequest;
  const findingsRows = safeRows<InitialInspectionFindingsRow>(iir.findings_rows);
  const replaceRows = safeRows<InitialInspectionPartLineRow>(iir.parts_replace_rows);
  const repairRows = safeRows<InitialInspectionPartLineRow>(iir.parts_repair_rows);

  const mainLines =
    findingsRows.length > 0
      ? findingsRows.map((row, index) => ({
          lineNumber: row.s_no || index + 1,
          description: row.finding || ' ',
          cells: { action: row.action_to_be_taken || ' ' },
        }))
      : [
          {
            lineNumber: 1,
            description: iir.scope_of_work_text || iir.findings || iir.observations || '—',
            cells: { action: '—' },
          },
        ];

  const partsBlock = [
    replaceRows.length
      ? `Parts to be Replaced:\n${replaceRows
          .map(
            (r, i) =>
              `${r.s_no || i + 1}. ${r.part || '—'} · Qty ${[r.qty, r.uom].filter(Boolean).join(' ') || '—'}`
          )
          .join('\n')}`
      : '',
    repairRows.length
      ? `Parts to be Repaired / Machined:\n${repairRows
          .map(
            (r, i) =>
              `${r.s_no || i + 1}. ${r.part || '—'} · Qty ${[r.qty, r.uom].filter(Boolean).join(' ') || '—'}`
          )
          .join('\n')}`
      : '',
  ]
    .filter(Boolean)
    .join('\n\n');

  return (
    <EngineCommercialA4Print
      docType="initial_inspection"
      title={iir.report_title || 'Initial Job Inspection Report'}
      documentNumber={iir.report_no || iir.iir_number || 'IIR'}
      partyName={iir.customer_name || ''}
      onClose={props.onClose}
      introText={iir.report_subtitle || undefined}
      metaLines={[
        { label: 'Customer', value: iir.customer_name || '' },
        { label: 'Report No', value: iir.report_no || iir.iir_number || '' },
        { label: 'Job No', value: iir.job_no || '' },
        { label: 'WR No', value: iir.wr_no || '' },
        {
          label: 'Date',
          value: iir.report_date ? dayjs(iir.report_date).format('DD-MMM-YYYY') : '',
        },
        { label: 'Client Ref 1', value: iir.client_ref_1 || '' },
        { label: 'Make', value: iir.make || '' },
        { label: 'PO No', value: iir.po_no || '' },
        { label: 'Work Description', value: iir.work_description || '' },
        { label: 'Client Ref 2', value: iir.client_ref_2 || '' },
        { label: 'Gate Pass No', value: iir.gate_pass_no || '' },
        { label: 'Qty No', value: iir.qty_no || '' },
        { label: 'Equipment Name', value: iir.equipment_name || '' },
        { label: 'Scope of Work', value: iir.scope_of_work_text || '' },
      ].filter((m) => m.value)}
      columns={[
        { key: 'sno', label: 'S-No' },
        { key: 'description', label: 'Findings' },
        { key: 'action', label: 'Action to be taken' },
      ]}
      lines={mainLines}
      finals={[
        ...(partsBlock ? [{ id: 'parts', heightPx: 90 }] : []),
        { id: 'remarks', heightPx: 48 },
        { id: 'sign', heightPx: 72 },
      ]}
      finalBlocks={{
        parts: partsBlock ? (
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 11 }}>
            {partsBlock}
          </pre>
        ) : null,
        remarks: (
          <div>
            <strong>Remarks</strong>
            <p>{iir.remarks || iir.findings || iir.observations || ' '}</p>
          </div>
        ),
        sign: (
          <table style={{ width: '100%', fontSize: 11 }}>
            <thead>
              <tr>
                <th />
                <th>Prepared By</th>
                <th>Approved By</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>Signature</th>
                <td>_________________</td>
                <td>_________________</td>
              </tr>
              <tr>
                <th>Print Name</th>
                <td>{iir.prepared_by_print_name || ' '}</td>
                <td>{iir.approved_by_print_name || ' '}</td>
              </tr>
              <tr>
                <th>Designation</th>
                <td>{iir.prepared_by_designation || ' '}</td>
                <td>{iir.approved_by_designation || ' '}</td>
              </tr>
            </tbody>
          </table>
        ),
      }}
    />
  );
};
